import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, LayoutGridIcon, MapIcon, PresentationIcon } from 'lucide-react';
import { chapters, entry } from '../../data/journey';
interface EntryScreenProps {
  onStartCampaign: (snak: 1 | 2 | 3) => void;
  onStartPresentation: () => void;
  onOpenJourney: () => void;
  onOpenStoryboard: () => void;
  /** Storyboard only — renders a specific state of the entry screen. */
  previewState?: 'cover' | 'menu';
}
const EASE = [0.23, 1, 0.32, 1] as const;
const DOT: Record<string, string> = {
  none: 'bg-line',
  colour: 'bg-ozBlue',
  full: 'bg-ozGreen'
};
export function EntryScreen({
  onStartCampaign,
  onStartPresentation,
  onOpenJourney,
  onOpenStoryboard,
  previewState
}: EntryScreenProps) {
  const [menuOpen, setMenuOpen] = useState(previewState === 'menu');
  return <AnimatePresence mode="wait" data-id="element-562" data-mp="37zv12xlpkk3x">
      {!menuOpen ? <motion.div key="cover" initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.24,
      ease: EASE
    }} className="flex flex-1 flex-col justify-center" data-id="element-563" data-mp="37x8hfzljme5c">
          <p className="text-[9.5px] uppercase tracking-micro text-blue" data-id="element-564" data-mp="3o0l46rp3xosg">{entry.eyebrow}</p>

          <h2 className="mt-4 font-display text-[32px] font-semibold leading-[1.02] tracking-tight text-navy" data-id="element-565" data-mp="3t222cfg7ix5c">
            {entry.headline[0]}
            <br data-id="element-566" data-mp="1t4z6z72s936" />
            {entry.headline[1]}
          </h2>

          <p className="mt-4 text-[13px] leading-relaxed text-mut" data-id="element-567" data-mp="5v03mckaz2l6">{entry.lead}</p>

          <div className="mt-8 flex flex-col gap-2" data-id="element-568" data-mp="aokrhbs6mg6n">
            <button type="button" onClick={() => setMenuOpen(true)} className="flex h-12 items-center justify-between gap-2 rounded-full bg-navy px-5 text-left transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2" data-id="element-569" data-mp="fmcsqwepveni">
              <span data-id="element-570" data-mp="3w2allzwzeh46">
                <span className="block text-[13.5px] font-semibold leading-none text-paper" data-id="element-571" data-mp="3vzodnz66hzlf">
                  {entry.openLabel}
                </span>
                <span className="mt-1 block text-[10px] leading-none text-sky" data-id="element-572" data-mp="9n9gfm445fx6">
                  {entry.openDetail}
                </span>
              </span>
              <ArrowRightIcon className="h-4 w-4 shrink-0 text-mint" aria-hidden="true" data-id="element-573" data-mp="imw83g71j7d1" />
            </button>

            <div className="flex gap-2" data-id="element-574" data-mp="do1pae92pisx">
              <button type="button" onClick={onOpenJourney} className="flex h-11 flex-1 items-center justify-center gap-2 rounded-full border border-line text-[12px] font-medium text-navy transition-colors duration-150 hover:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-575" data-mp="dn02xdmlx9vw">
                <MapIcon className="h-3.5 w-3.5 text-blue" aria-hidden="true" data-id="element-576" data-mp="2pdvmq4pmodd5" />
                {entry.journeyLabel}
              </button>
              <button type="button" onClick={onOpenStoryboard} className="flex h-11 flex-1 items-center justify-center gap-2 rounded-full border border-line text-[12px] font-medium text-navy transition-colors duration-150 hover:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-577" data-mp="14vti08fwm0or">
                <LayoutGridIcon className="h-3.5 w-3.5 text-blue" aria-hidden="true" data-id="element-578" data-mp="m4k6jtmkp1uj" />
                {entry.storyboardLabel}
              </button>
            </div>
          </div>

          <p className="mt-auto pt-6 text-[9px] leading-snug text-mut" data-id="element-579" data-mp="c6w3mgetco1a">{entry.footnote}</p>
        </motion.div> : <motion.div key="menu" initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.24,
      ease: EASE
    }} className="flex flex-1 flex-col" data-id="element-580" data-mp="h6s62b7v47eq">
          <button type="button" onClick={() => setMenuOpen(false)} className="flex items-center gap-1.5 self-start text-[11px] text-mut transition-colors duration-150 hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-581" data-mp="bhie0ncs5b68">
            <ArrowLeftIcon className="h-3 w-3" aria-hidden="true" data-id="element-582" data-mp="3tn3ts9q5o3y3" />
            {entry.backLabel}
          </button>

          <h2 className="mt-3 font-display text-[20px] font-semibold leading-snug text-navy" data-id="element-583" data-mp="3ook7u9bkas5q">
            {entry.menuTitle}
          </h2>

          <p className="mt-4 text-[9px] uppercase tracking-micro text-mut" data-id="element-584" data-mp="3jvr49ortxpv8">{entry.campaignLabel}</p>

          <ul className="mt-2.5 flex flex-col gap-1.5" data-id="element-585" data-mp="3eyqtx1ny60yb">
            {chapters.map((chapter, i) => <motion.li key={chapter.snak} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.22,
          delay: 0.04 + i * 0.05,
          ease: EASE
        }} data-id="element-586" data-mp="3aygrfyzd5so7" data-mpi={i}>
                <button type="button" onClick={() => onStartCampaign(chapter.snak)} className="flex w-full items-start gap-3 rounded-xl border border-line bg-mist px-3.5 py-3 text-left transition-colors duration-150 hover:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-587" data-mp="1ggx6wsgoblq4">
                  <span className="min-w-0 flex-1" data-id="element-588" data-mp="1udu4dcras1fz">
                    <span className="flex items-center gap-2" data-id="element-589" data-mp="xr7bq4900xxo">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${DOT[chapter.brand]}`} aria-hidden="true" data-id="element-590" data-mp="12oqzburl6n2a" />
                      <span className="font-display text-[13.5px] font-semibold leading-none tracking-tight text-navy" data-id="element-591" data-mp="1bhaugl8o69id">
                        {chapter.archetype}
                      </span>
                      <span className="text-[9px] uppercase tracking-micro text-mut" data-id="element-592" data-mp="exlk9htgzyt1">
                        {chapter.label}
                      </span>
                    </span>
                    <span className="mt-1.5 block text-[11.5px] leading-snug text-navy2" data-id="element-593" data-mp="1m9ugh977pxqn">
                      {chapter.verb} · {chapter.differentiation}
                    </span>
                    <span className="mt-1 block text-[10.5px] leading-snug text-mut" data-id="element-594" data-mp="1w179430u4rxq">
                      {chapter.audience}
                    </span>
                    <span className="mt-1.5 block text-[9px] uppercase tracking-micro text-mut" data-id="element-595" data-mp="13ctcnrpea3oa">
                      {chapter.brandNote}
                    </span>
                  </span>
                  <ArrowRightIcon className="mt-1 h-4 w-4 shrink-0 text-navy2" aria-hidden="true" data-id="element-596" data-mp="1cdrcdxssvp12" />
                </button>
              </motion.li>)}
          </ul>

          <div className="mt-auto pt-5" data-id="element-597" data-mp="18e8s948szhza">
            <button type="button" onClick={onStartPresentation} className="flex w-full items-center gap-3 rounded-xl bg-navy px-4 py-3 transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2" data-id="element-598" data-mp="2ns5092nk04h3">
              <PresentationIcon className="h-4 w-4 shrink-0 text-mint" aria-hidden="true" data-id="element-599" data-mp="2wbi1k8g5n6bl" />
              <span className="min-w-0 flex-1 text-left" data-id="element-600" data-mp="2hdxyj72tjl82">
                <span className="block text-[12.5px] font-semibold leading-snug text-paper" data-id="element-601" data-mp="2mbhimnm76s7x">
                  {entry.presentationLabel}
                </span>
                <span className="mt-0.5 block text-[10.5px] leading-snug text-sky" data-id="element-602" data-mp="3a3eqy4qfv24a">
                  {entry.presentationDetail}
                </span>
              </span>
            </button>
          </div>
        </motion.div>}
    </AnimatePresence>;
}